// Auth context. Wraps Supabase auth state and provides login/logout/session management to the app.
import React, { createContext, useContext, useEffect, useState } from 'react';
import { 
  type User,
  type Session
} from '@supabase/supabase-js';
import { supabase } from '../config/supabase';
import { API_URL } from '../services/apiClient';

interface AuthContextType {
  currentUser: User | null;
  session: Session | null;
  login: (email: string, password: string, role?: string) => Promise<void>;
  signup: (email: string, password: string, role?: string) => Promise<void>;
  loginWithGoogle: (role?: string) => Promise<void>;
  mockLogin: (role: string) => Promise<void>;
  logout: () => Promise<void>;
  loading: boolean;
  error: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Basic email format validation (no domain restriction)
const isValidEmail = (email: string): boolean => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email.trim());
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Check for localStorage auth on mount
  useEffect(() => {
    const checkLocalStorageAuth = () => {
      const authToken = sessionStorage.getItem('authToken');
      const userRole = localStorage.getItem('userRole');
      const userStr = localStorage.getItem('user');
      
      if (authToken && userRole && userStr) {
        try {
          const user = JSON.parse(userStr);
          // Create a mock user object for localStorage auth
          const mockUser = {
            id: user.id || 'local-user',
            email: user.email,
            app_metadata: { provider: 'local' },
            user_metadata: { full_name: user.name },
            aud: 'authenticated',
            created_at: new Date().toISOString(),
            role: 'authenticated'
          };
          setCurrentUser(mockUser);
          setLoading(false);

          // Notify Electron on session restore so it starts socket + monitoring
          if (window.electronAPI?.setUserRole) {
            window.electronAPI.setUserRole({ role: userRole, token: authToken });
          }

          return true; // Found localStorage auth
        } catch (err) {
          console.error('Failed to parse localStorage user:', err);
        }
      }
      return false; // No localStorage auth
    };

    const hasLocalStorageAuth = checkLocalStorageAuth();
    
    // Only check Supabase if no localStorage auth AND supabase is configured
    if (!hasLocalStorageAuth && supabase) {
      const { data: { subscription } } = supabase.auth.onAuthStateChange((event: import('@supabase/supabase-js').AuthChangeEvent, session: import('@supabase/supabase-js').Session | null) => {
        
        setSession(session);
        setCurrentUser(session?.user ?? null);
        
        if (event === 'SIGNED_IN' && session?.user) {
          syncUser(session.user);
        }

        setLoading(false);
      });

      return () => subscription.unsubscribe();
    } else if (!hasLocalStorageAuth) {
      // No localStorage auth and no Supabase - just finish loading
      setLoading(false);
    }
  }, []);

  const login = async (email: string, password: string, role?: string): Promise<void> => {
    try {
      setError(null);
      setLoading(true);
      
      // Optional: basic email validation
      if (!isValidEmail(email)) {
        throw new Error('Please enter a valid email address.');
      }

      // Store the selected role in localStorage for App.tsx to use
      if (role) {
        localStorage.setItem('userRole', role);
      }
      
      if (!supabase) {
        throw new Error('Authentication not configured. Please use system/school admin login or contact support.');
      }
      
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (error) throw error;
      
    } catch (error: unknown) {
      console.error('Login error:', error);
      const message = error instanceof Error ? error.message : 'An error occurred during login.';
      setError(message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const signup = async (email: string, password: string): Promise<void> => {
    try {
      setError(null);
      setLoading(true);
      
      // Optional: basic email validation
      if (!isValidEmail(email)) {
        throw new Error('Please enter a valid email address.');
      }
      
      if (!supabase) {
        throw new Error('Authentication not configured. Please contact support.');
      }
      
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback`
        }
      });
      
      if (error) throw error;
      
    } catch (error: unknown) {
      console.error('Signup error:', error);
      const message = error instanceof Error ? error.message : 'An error occurred during signup.';
      setError(message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const loginWithGoogle = async (role?: string): Promise<void> => {
    try {
      setError(null);
      setLoading(true);
      
      // Store the selected role before OAuth flow
      if (role) {
        localStorage.setItem('userRole', role);
      }

      if (!supabase) {
        throw new Error('Authentication not configured. Please contact support.');
      }

      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`
        }
      });
      
      if (error) throw error;
      
      // Note: Email domain validation for OAuth will be handled in the auth callback
      // since we don't get the user data immediately
      
    } catch (error: unknown) {
      console.error('Google login error:', error);
      const message = error instanceof Error ? error.message : 'An error occurred during Google login.';
      setError(message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const mockLogin = async (role: string): Promise<void> => {
    setLoading(true);
    localStorage.setItem('userRole', role);
    
    // Create a mock user that satisfies the User type (partially)
    const mockUser = {
      id: 'mock-user-id',
      email: role === 'teacher' ? 'teacher@lincolnhigh.edu' : 'student@lincolnhigh.edu',
      app_metadata: { provider: 'email' },
      user_metadata: { full_name: role === 'teacher' ? 'Mrs. Anderson' : 'Sarah Mitchell' },
      aud: 'authenticated',
      created_at: new Date().toISOString(),
      role: 'authenticated'
    } as unknown as User;
    
    setCurrentUser(mockUser);
    setLoading(false);
  };

  const logout = async (): Promise<void> => {
    try {
      setError(null);
      
      // Check if user is authenticated via sessionStorage/localStorage
      const authToken = sessionStorage.getItem('authToken');
      const userRole = localStorage.getItem('userRole');
      
      if (authToken && userRole) {
        // Best-effort server-side logout so Redis-backed system admin sessions are invalidated.
        await fetch(`${API_URL}/auth/logout`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }).catch((logoutError) => {
          console.warn('Server logout failed, continuing local logout:', logoutError);
        });

        // Clear auth data
        sessionStorage.removeItem('authToken');
        sessionStorage.removeItem('refreshToken');
        localStorage.removeItem('userRole');
        localStorage.removeItem('user');
        setCurrentUser(null);
      } else if (supabase) {
        // Clear Supabase auth
        const { error } = await supabase.auth.signOut();
        if (error) throw error;
      } else {
        // No auth provider - just clear state
        setCurrentUser(null);
        setSession(null);
      }
    } catch (error: unknown) {
      console.error('Logout error:', error);
      const message = error instanceof Error ? error.message : 'An error occurred during logout.';
      setError(message);
      throw error;
    }
  };

  const syncUser = async (user: User) => {
    try {
      const response = await fetch(`${API_URL}/auth/sync`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: user.email,
          authUid: user.id,
          name: user.user_metadata?.full_name
        }),
      });

      const data = await response.json();
      if (data.success && data.user) {
        localStorage.setItem('userRole', data.user.role);
      }
    } catch (error) {
      console.error('Failed to sync user:', error);
    }
  };

  useEffect(() => {
    // Skip if Supabase is not configured
    if (!supabase) {
      return;
    }

    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }: { data: { session: import('@supabase/supabase-js').Session | null } }) => {
      setSession(session);
      setCurrentUser(session?.user ?? null);
      if (session?.user) {
        syncUser(session.user);
      }
      setLoading(false);
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event: import('@supabase/supabase-js').AuthChangeEvent, session: import('@supabase/supabase-js').Session | null) => {
      
      setSession(session);
      setCurrentUser(session?.user ?? null);
      
      if (event === 'SIGNED_IN' && session?.user) {
        syncUser(session.user);
      }

      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const value: AuthContextType = {
    currentUser,
    session,
    login,
    signup,
    loginWithGoogle,
    mockLogin,
    logout,
    loading,
    error
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};